<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import {
  movedTopicIds,
  useSidebarTopicDialog,
  useSidebarTopicMenu,
} from 'dashboard/composables/useSidebarTopics';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import MenuItem from 'dashboard/components/widgets/conversation/contextMenu/menuItem.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const { t } = useI18n();
const topics = useMapGetter('mhTopics/getTopics');
const { topicMenuState, closeTopicMenu } = useSidebarTopicMenu();
const { openEditTopicDialog } = useSidebarTopicDialog();

const topicIndex = computed(() =>
  topics.value.findIndex(topic => topic.id === topicMenuState.value?.id)
);
const topic = computed(() => topics.value[topicIndex.value] || null);

const moveUpOption = computed(() => ({
  label: t('MH_TOPICS.MENU.MOVE_UP'),
  icon: 'chevron-up',
}));
const moveDownOption = computed(() => ({
  label: t('MH_TOPICS.MENU.MOVE_DOWN'),
  icon: 'chevron-down',
}));
const editOption = computed(() => ({
  label: t('MH_TOPICS.MENU.EDIT'),
  icon: 'edit',
}));
const deleteOption = computed(() => ({
  label: t('MH_TOPICS.MENU.DELETE'),
  icon: 'delete',
}));

const deleteDialogRef = ref(null);
const topicToDelete = ref(null);
const isDeleting = ref(false);

const onMove = async delta => {
  const orderedIds = movedTopicIds(topics.value, topic.value.id, delta);
  closeTopicMenu();
  if (!orderedIds) return;
  try {
    await store.dispatch('mhTopics/reorder', orderedIds);
  } catch (error) {
    if (error?.response?.status === 409) {
      useAlert(t('MH_TOPICS.ERRORS.STALE_ORDER'));
    } else {
      useAlert(t('MH_TOPICS.ERRORS.REORDER'));
    }
  }
};

const onEdit = () => {
  const topicId = topic.value.id;
  closeTopicMenu();
  openEditTopicDialog(topicId);
};

const onDelete = () => {
  topicToDelete.value = topic.value;
  closeTopicMenu();
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  if (!topicToDelete.value || isDeleting.value) return;
  isDeleting.value = true;
  try {
    await store.dispatch('mhTopics/delete', topicToDelete.value);
    deleteDialogRef.value?.close();
  } catch (error) {
    if (error?.response?.status === 409) {
      await store.dispatch('mhTopics/get');
      useAlert(t('MH_TOPICS.ERRORS.STALE_OBJECT'));
    } else {
      useAlert(t('MH_TOPICS.ERRORS.DELETE'));
    }
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <ContextMenu
    v-if="topicMenuState && topic"
    :x="topicMenuState.x"
    :y="topicMenuState.y"
    @close="closeTopicMenu"
  >
    <div
      class="p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50"
    >
      <MenuItem
        v-if="topicIndex > 0"
        :option="moveUpOption"
        variant="icon"
        @click.stop="onMove(-1)"
      />
      <MenuItem
        v-if="topicIndex < topics.length - 1"
        :option="moveDownOption"
        variant="icon"
        @click.stop="onMove(1)"
      />
      <MenuItem :option="editOption" variant="icon" @click.stop="onEdit" />
      <MenuItem :option="deleteOption" variant="icon" @click.stop="onDelete" />
    </div>
  </ContextMenu>
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('MH_TOPICS.DELETE.TITLE')"
    :description="t('MH_TOPICS.DELETE.DESCRIPTION')"
    :confirm-button-label="t('MH_TOPICS.DELETE.CONFIRM')"
    :cancel-button-label="t('MH_TOPICS.DIALOG.CANCEL')"
    :is-loading="isDeleting"
    width="md"
    @confirm="confirmDelete"
  />
</template>
